import type { CreateExamFormValues } from "./CreateExamForm";
import { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import Typography from "@mui/material/Typography";
import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { LinearProgress } from "react-admin";
import { getPriceSuggestion, GetPriceSuggestionRequestSchema } from "../../api/backoffice.api";
import { Money } from "../../model/Money";
import { moneyFormatter } from "../../misc/Money";
import { Row } from "../../misc/Row";
import { Column } from "../../misc/Column";
import { BookedTraining } from "../../model/BookedTraining";
import { type DrivingLicenseClass, isForMotorcycle } from "../../model/DrivingLicenseClass";

interface PriceEntry {
  bookedTrainingId: string;
  drivingLicenseClass: DrivingLicenseClass;
  price: Money;
  authorityFee?: Money;
}

type LookupStatus = "idle" | "loading" | "success" | "error";

const LOOKUP_FAILED_MESSAGE = "Preise konnten nicht geladen werden.";

export function TheoryExamPriceSelector(props: { activeBookedTrainings: BookedTraining[] }) {
  const { activeBookedTrainings } = props;
  const { setValue, watch, setError, clearErrors } = useFormContext<CreateExamFormValues>();
  const { bookedTrainingIds, student, dateTime, theoryExamPriceSelection: selection } = watch();
  const [status, setStatus] = useState<LookupStatus>("loading");
  const [entries, setEntries] = useState<PriceEntry[]>([]);
  const priceSuggestionCache = useRef<Record<string, ReturnType<typeof getPriceSuggestion>>>({}).current;

  const studentId = student?.id;

  useEffect(() => {
    if (!studentId || !dateTime?.isValid || bookedTrainingIds.length < 2) {
      setEntries([]);
      setStatus("idle");
      return;
    }
    setStatus("loading");
    const requests = bookedTrainingIds.map((bookedTrainingId) => {
      const request = GetPriceSuggestionRequestSchema.parse({
        eventType: "DrivingLesson",
        drivingLessonType: "theoretischePruefung",
        studentUid: studentId,
        bookedTrainingId,
        start: dateTime,
      });
      const cacheKey = JSON.stringify(request);
      let promise = priceSuggestionCache[cacheKey];
      if (!promise) {
        // Evict rejected requests from the cache so subsequent lookups can retry.
        promise = getPriceSuggestion(request).catch((error) => {
          delete priceSuggestionCache[cacheKey];
          throw error;
        });
        priceSuggestionCache[cacheKey] = promise;
      }
      return promise.then((suggestion) => ({
        bookedTrainingId,
        drivingLicenseClass: activeBookedTrainings.find((it) => it.id === bookedTrainingId)!.drivingLicenseClass,
        price: suggestion.price,
        authorityFee: suggestion.authorityFee,
      }));
    });
    let cancelled = false;
    void Promise.all(requests)
      .then((results) => {
        if (!cancelled) {
          setEntries(results);
          setStatus("success");
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.error("Failed to get price suggestions for multi-training theory exam", error);
          setEntries([]);
          setStatus("error");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [studentId, dateTime, bookedTrainingIds]);

  // Invalidate a stale `theoryExamPriceSelection` so we never submit an obsolete `manualPrice`:
  // - clear immediately if the selected training was removed,
  // - after each price lookup: clear if the selected training's current price no longer matches,
  // - clear if the entries are now uniform (no manager choice is needed anymore).
  useEffect(() => {
    if (!selection) return;
    if (!bookedTrainingIds.includes(selection.bookedTrainingId)) {
      setValue("theoryExamPriceSelection", null);
      return;
    }
    const entry = entries.find((e) => e.bookedTrainingId === selection.bookedTrainingId);
    const distinctPriceCount = new Set(entries.map((it) => it.price.amount)).size;
    const isStale = !entry || entry.price.amount !== selection.price.amount || distinctPriceCount <= 1;
    if (isStale) {
      setValue("theoryExamPriceSelection", null);
    }
  }, [entries, bookedTrainingIds, selection, setValue]);

  // Block submission until the price is known:
  // - while requests are pending, keep a "loading" error,
  // - if the lookup failed, keep an "error" error until entries change (retry succeeds or N<2),
  // - on success: require a manual selection when prices differ; otherwise clear the error.
  useEffect(() => {
    if (status === "loading") {
      setError("theoryExamPriceSelection", { type: "loading", message: "Preise werden geladen…" });
    } else if (status === "error") {
      setError("theoryExamPriceSelection", { type: "lookupFailed", message: LOOKUP_FAILED_MESSAGE });
    } else {
      // status === "idle" || status === "success"
      const requiresSelection = new Set(entries.map((it) => it.price.amount)).size > 1;
      if (requiresSelection && !selection) {
        setError("theoryExamPriceSelection", { type: "required", message: "Bitte wähle einen Preis aus." });
      } else {
        clearErrors("theoryExamPriceSelection");
      }
    }
    return () => {
      clearErrors("theoryExamPriceSelection");
    };
  }, [status, entries, selection, setError, clearErrors]);

  if (status === "loading") {
    return (
      <Row sx={{ mb: 1 }}>
        <LinearProgress sx={{ width: "4em" }} timeout={0} />
      </Row>
    );
  }

  if (status === "error") {
    return (
      <Typography variant="body2" color="error">
        {LOOKUP_FAILED_MESSAGE}
      </Typography>
    );
  }

  // No entries (trainings dropped below 2) — render nothing rather than an empty dropdown.
  if (entries.length === 0) {
    return null;
  }

  // Uniform-price case: no manager choice needed — render like PriceSuggestion.
  const distinctPriceCount = new Set(entries.map((it) => it.price.amount)).size;
  if (distinctPriceCount <= 1) {
    const primaryTraining = entries.find((it) => isForMotorcycle(it.drivingLicenseClass)) ?? entries[0];
    return (
      <Row sx={{ alignItems: "center", "& .MuiTypography-root": { fontSize: "14px" } }}>
        <Typography>{"Preis:\u00A0 "}</Typography>
        <Typography>{moneyFormatter.format(primaryTraining.price.amount)}</Typography>
        {primaryTraining.authorityFee && (
          <>
            <Typography>{"\u00A0+\u00A0"}</Typography>
            <Typography>{`TÜV-/DEKRA-Gebühr: ${moneyFormatter.format(primaryTraining.authorityFee.amount)}`}</Typography>
          </>
        )}
      </Row>
    );
  }

  const selectedEntry = selection ? entries.find((e) => e.bookedTrainingId === selection.bookedTrainingId) : undefined;

  return (
    <Column sx={{ gap: "4px" }}>
      <FormControl size="small" sx={{ minWidth: 240 }}>
        <InputLabel id="theory-exam-price-selector-label">Preis</InputLabel>
        <Select
          labelId="theory-exam-price-selector-label"
          label="Preis"
          value={selection?.bookedTrainingId ?? ""}
          onChange={(event) => {
            const bookedTrainingId = event.target.value as string;
            const entry = entries.find((e) => e.bookedTrainingId === bookedTrainingId);
            if (entry) {
              setValue("theoryExamPriceSelection", {
                bookedTrainingId: entry.bookedTrainingId,
                price: {
                  amount: entry.price.amount + (entry.authorityFee?.amount ?? 0),
                  currency: entry.price.currency,
                },
              });
            }
          }}
          data-testid="theory-exam-price-selector"
        >
          {entries.map((entry) => (
            <MenuItem key={entry.bookedTrainingId} value={entry.bookedTrainingId}>
              {`Klasse ${entry.drivingLicenseClass}: ${moneyFormatter.format(entry.price.amount)}`}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {selectedEntry?.authorityFee && (
        <Typography variant="body2">{`+\u00A0TÜV-/DEKRA-Gebühr: ${moneyFormatter.format(selectedEntry.authorityFee.amount)}`}</Typography>
      )}
    </Column>
  );
}
